import React, { useState, useEffect, lazy, Suspense } from "react";
import { sections } from './data';
import {
  FaEnvelope,
  FaGithub,
  FaLinkedin,
  FaMedium,
  FaBookOpen,
  FaHome,
} from "react-icons/fa";
import Seo from "./Seo";

// Lazy load components
const Nova = lazy(() => import("./AgentNova.jsx"));

// Social link component with improved accessibility
const SocialLink = ({ href, icon, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-black hover:text-green-800 transition transform hover:-translate-y-1 duration-300"
    aria-label={label}
  >
    {icon}
  </a>
);

// Extracted reusable components with improved accessibility
const SocialLinks = () => (
  <div className="flex justify-center gap-4 mt-4 mb-4">
    <SocialLink 
      href="mailto:iamrahul@tutamail.com" 
      icon={<FaEnvelope className="text-2xl" />} 
      label="Email Rahul"
    />
    <SocialLink 
      href="https://github.com/iamrahulreddy" 
      icon={<FaGithub className="text-2xl" />} 
      label="Rahul's GitHub"
    />
    <SocialLink 
      href="https://www.linkedin.com/in/iamrahulreddy/" 
      icon={<FaLinkedin className="text-2xl" />} 
      label="Rahul's LinkedIn"
    />
    <SocialLink 
      href="https://rahulreddy.dev/" 
      icon={<FaMedium className="text-2xl" />} 
      label="Rahul's Website"
    />
  </div>
);

// Article card component with improved accessibility and contrast
const ArticleCard = ({ item, section, style }) => (
  <article
    className="bg-white p-6 rounded-xl shadow-lg transform transition-all duration-300 ease-in-out hover:-translate-y-2 hover:shadow-2xl flex flex-col h-full"
    style={{
      borderTop: `4px solid var(--color-${section.color}-500)`,
      ...style
    }}
  >
    <header className="mb-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold" style={{ color: `var(--color-${section.color}-600)` }}>{item.title}</h3>
        <span className="ml-3 flex-shrink-0" style={{ color: `var(--color-${section.color}-600)` }}>
          <item.icon className="text-3xl" aria-hidden="true" />
        </span>
      </div>
    </header>

    <p className="text-gray-800 mb-6 text-sm flex-grow">
      {item.description}
    </p>

    <div className="flex justify-between items-center mt-auto pt-2">
      <a
        href={item.link}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium flex items-center gap-1 relative group"
        style={{ color: `var(--color-${section.color}-600)` }}
        aria-label={`Read full article about ${item.title}`}
      >
        <span className="group-hover:translate-x-1 transition-transform duration-300">Read Article</span>
        <span className="absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300" 
              style={{ backgroundColor: `var(--color-${section.color}-600)` }}></span>
      </a>
      <a
        href={item.visualizeLink}
        target="_blank"
        rel="noopener noreferrer"
        className="px-4 py-2 rounded-full text-sm transition-all duration-300 text-white font-medium transform hover:-translate-y-1 hover:shadow-md"
        style={{ 
          backgroundColor: `var(--color-${section.color}-600)`, 
        }}
        aria-label={`View visualization for ${item.title}`}
      >
        Visualize
      </a>
    </div>
  </article>
);

// View all button component with improved contrast
const ViewAllButton = ({ section }) => (
  <a
    href={section.viewAllLink}
    className="text-white px-5 py-2 rounded-full text-sm flex items-center gap-2 transition shadow-md overflow-hidden relative group"
    style={{ backgroundColor: `var(--color-${section.color}-600)` }}
    aria-label={`View all ${section.name} visualizations`}
  >
    <span className="relative z-10 group-hover:translate-x-1 transition-transform duration-300">View All</span>
    <FaBookOpen className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform duration-300" aria-hidden="true" />
    <span className="absolute inset-0 w-full h-full bg-white transform scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 opacity-20"></span>
  </a>
);

// Section display component
const SectionDisplay = ({ section, items }) => {
  if (items.length === 0) return null;

  return (
    <section id={section.id} className="mb-16">
      <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: `var(--color-${section.color}-500)` }}>
        {section.name}
      </h2>
      <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-4 sm:grid-cols-1">
        {items.map((item, index) => (
          <ArticleCard 
            key={index} 
            item={item} 
            section={section}
            style={{ animationDelay: `${index * 0.1}s` }}
          />
        ))}
      </div>
      <div className="flex justify-end mt-6">
        <ViewAllButton section={section} />
      </div>
    </section>
  );
};

// Header component with improved semantics and larger icon
const Header = () => (
  <header className="mb-8 text-center max-w-4xl mx-auto">
    <h1 className="text-xl font-bold mb-4 relative inline-block">
      <span className="bg-gradient-to-r from-purple-600 via-pink-500 to-red-500 text-transparent bg-clip-text">
        Project Graphil
      </span>
      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 via-pink-500 to-red-500 group-hover:w-full transition-all duration-700 ease-in-out animate-expand-underline"></span>
    </h1>
    <p className="text-gray-800 text-sm leading-relaxed">
      Hey there 👋, I'm Rahul! Welcome to <strong>Project Graphil</strong> — a visual companion to my blog{" "}
      <a
        href="https://neuralnets.dev"
        className="text-blue-600 hover:text-white hover:bg-gradient-to-r from-gray-700 via-gray-900 to-black underline transition-colors"
        target="_blank"
        rel="noopener noreferrer"
      >
        neuralnets.dev
      </a>. These visual guides simplify technical concepts with diagrams—because visuals beat words (and typos!) every time 😉.
    </p>
  </header>
);

// Footer component with improved accessibility
const Footer = () => (
  <footer className="mx-auto px-2 py-2 font-mono text-center mt-16">
    <h2 className="text-2xl font-bold mb-2 text-indigo-800">Reach Out</h2>
    <p className="text-gray-800 text-md mb-1">
      Have questions, suggestions, or just want to say hi? Whether it's a
      discussion about the intricacies of Machine Learning, dissecting
      cybersecurity vulnerabilities, or debating the art of meme creation 😎,
      I'm all ears.
    </p>
    <SocialLinks />
    <p className="text-gray-800">
      Crafted with ❤️ by{" "}
      <span className="text-red-600 underline">Muskula Rahul</span>
    </p>
    <p className="text-gray-700 mt-2">© {new Date().getFullYear()} Project Graphil</p>
  </footer>
);

// No results component
const NoResults = () => (
  <div className="text-center py-12">
    <p className="text-gray-800 text-lg">No matching visualizations found.</p>
  </div>
);

// CSS Variables for improved theming and performance
const CSSVariables = () => (
  <style jsx global>{`
    :root {
      --color-red-500: #ef4444;
      --color-red-600: #dc2626;
      --color-red-700: #b91c1c;
      --color-red-800: #991b1b;
      --color-blue-100: #dbeafe;
      --color-blue-500: #3b82f6;
      --color-blue-600: #2563eb;
      --color-blue-700: #1d4ed8;
      --color-blue-800: #1e40af;
      --color-green-100: #d1fae5;
      --color-green-500: #10b981;
      --color-green-600: #059669;
      --color-green-700: #047857;
      --color-green-800: #065f46;
      --color-purple-100: #ede9fe;
      --color-purple-500: #8b5cf6;
      --color-purple-600: #7c3aed;
      --color-purple-700: #6d28d9;
      --color-purple-800: #5b21b6;
      --color-yellow-100: #fef3c7;
      --color-yellow-500: #f59e0b;
      --color-yellow-600: #d97706;
      --color-yellow-700: #b45309;
      --color-yellow-800: #92400e;
      --color-pink-100: #fce7f3;
      --color-pink-500: #ec4899;
      --color-pink-600: #db2777;
      --color-pink-700: #be185d;
      --color-pink-800: #9d174d;
      --color-indigo-100: #e0e7ff;
      --color-indigo-500: #6366f1;
      --color-indigo-600: #4f46e5;
      --color-indigo-700: #4338ca;
      --color-indigo-800: #3730a3;
    }

    @keyframes expandUnderline {
      0% { width: 0; }
      100% { width: 100%; }
    }

    .animate-expand-underline {
      animation: expandUnderline 1.5s ease-in-out forwards;
    }

    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }

    article {
      animation: fadeInUp 0.5s ease-out forwards;
      opacity: 0;
    }

    /* Preload critical fonts */
    @font-face {
      font-family: 'Monospace';
      font-style: normal;
      font-weight: 400;
      font-display: swap;
      src: local('Monospace');
    }
  `}</style>
);

// Loading component with aria attributes
const LoadingSpinner = () => (
  <div className="text-center py-4" role="status" aria-live="polite">
    <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-indigo-600 rounded-full animate-spin"></div>
    <span className="sr-only">Loading Nova...</span>
  </div>
);

// Main App component - optimized
const App = () => {
  // Process sections data
  const sectionData = sections.map(section => {
    // Sort by date (newest first)
    const displayItems = [...section.items].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    ).slice(0, 3); // Limit to 3 items per section

    return { section, items: displayItems };
  });

  const hasContent = sectionData.some(data => data.items.length > 0);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Project Graphil",
    "description": "Explore visual guides and diagrams for AI, GCP, Computer Networks, Linux, and Python. A companion to neuralnets.dev with interactive visualizations.",
    "url": window.location.origin,
    "author": {
      "@type": "Person",
      "name": "Muskula Rahul",
      "url": "https://rahulreddy.dev/"
    }
  };

  return (
    <>
      <Seo
        title="Project Graphil - Visual Guides for AI, GCP, Networks, Linux and Python"
        description="Explore visual guides and diagrams for AI, GCP, Computer Networks, Linux, and Python. A companion to neuralnets.dev with interactive visualizations."
        name="Project Graphil"
        type="website"
        structuredData={structuredData}
      />

      <CSSVariables />

      <main className="max-w-full mx-auto px-4 py-10 font-mono">
        <Suspense fallback={<LoadingSpinner />}>
          <Nova />
        </Suspense>

        <Header />

        {hasContent ? (
          sectionData.map(({ section, items }) => (
            items.length > 0 && (
              <SectionDisplay
                key={section.id}
                section={section}
                items={items}
              />
            )
          ))
        ) : (
          <NoResults />
        )}

        <Footer />
      </main>
    </>
  );
};

// All topics page component - optimized
const AllTopicsPage = ({ section }) => {
  const sortedItems = [...section.items].sort((a, b) => new Date(b.date) - new Date(a.date));

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": section.name,
        "item": window.location.href
      }
    ]
  };

  const collectionData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${section.name} Visualizations - Project Graphil`,
    "description": `Explore all ${section.name} visualizations and diagrams on Project Graphil.`,
    "url": window.location.href,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Project Graphil",
      "url": window.location.origin
    }
  };

  return (
    <>
      <Seo
        title={`${section.name} Visualizations - Project Graphil`}
        description={`Explore all ${section.name} visualizations and diagrams on Project Graphil. Interactive learning guides for ${section.name.toLowerCase()} concepts.`}
        name="Project Graphil"
        type="website"
        structuredData={[breadcrumbData, collectionData]}
      />

      <CSSVariables />

      <main className="max-w-7xl mx-auto px-4 py-10 font-mono">
        <header className="text-center mb-12">
          <h1 className="text-xl font-bold mb-6" style={{ color: `var(--color-${section.color}-600)` }}>
            All {section.name} Visualizations
          </h1>
        </header>

        <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-4 sm:grid-cols-1">
          {sortedItems.map((item, index) => (
            <ArticleCard
              key={index}
              item={item}
              section={section}
              style={{animationDelay: `${index * 0.1}s`}}
            />
          ))}
        </div>

        <nav className="flex justify-center mt-12">
          <a
            href="/"
            className="px-6 py-3 rounded-full text-sm flex items-center gap-2 text-white transition-all duration-300 shadow-md relative overflow-hidden group"
            style={{ 
              background: `linear-gradient(to right, var(--color-${section.color}-600), var(--color-${section.color}-600))` 
            }}
            aria-label="Return to homepage"
          >
            <FaHome className="mr-2" aria-hidden="true" />
            <span className="relative z-10">Back to Home</span>
            <span className="absolute inset-0 w-full h-full bg-white transform scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 opacity-20"></span>
          </a>
        </nav>
      </main>
    </>
  );
};

export { App, AllTopicsPage };